// For operators
import debug from 'debug';

// Configure debug namespaces
const logOperators = debug('rois:actions:utils:operators');
const logVehicles = debug('rois:actions:utils:vehicles');
const logExternals = debug('rois:actions:utils:externals');
const logAnimals = debug('rois:actions:utils:animals');

debug.enable('rois:actions:utils:*');

export const findNewIDs = (
  existingIDs: string[],
  newIDs: string[]
): string[] => {
  const existingSet = new Set(existingIDs);

  const newIdArray = newIDs.filter((id) => !existingSet.has(id));

  logOperators(
    ` Encontrados ${newIdArray.length} nuevos operarios para añadir`
  );
  if (newIdArray.length > 0) {
    logOperators(' IDs de operarios a añadir:', newIdArray);
  }

  return newIdArray;
};

export const findRemovedIDs = (
  oldIDs: string[],
  newIDs: string[]
): string[] => {
  const newSet = new Set(newIDs);

  const removedIDs = oldIDs.filter((id) => !newSet.has(id));

  logOperators(` Encontrados ${removedIDs.length} operarios para eliminar`);
  if (removedIDs.length > 0) {
    logOperators(' IDs de operarios a eliminar:', removedIDs);
  }

  return removedIDs;
};

// For vehicles

export const findNewAndUpdatedVehicles = (
  currentVehicleData: {
    vehicleId: string;
    driverId: string;
    vehicleOccupantId: string;
  }[],
  newVehicleData: {
    vehicleId: string;
    driverId: string;
    vehicleOccupantId: string;
  }[]
) => {
  logVehicles(' Iniciando findNewAndUpdatedVehicles');
  logVehicles(
    ` Datos de entrada: ${currentVehicleData.length} vehículos actuales, ${newVehicleData.length} vehículos nuevos`
  );

  // Detailed logging of current vehicles
  logVehicles(' Vehículos actuales:');
  currentVehicleData.forEach((vehicle, idx) => {
    logVehicles(
      `  [${idx}] VehiculoID: ${vehicle.vehicleId}, ConductorID: ${vehicle.driverId}, OcupanteID: ${vehicle.vehicleOccupantId}`
    );
  });

  // Detailed logging of new vehicles
  logVehicles(' Vehículos nuevos:');
  newVehicleData.forEach((vehicle, idx) => {
    logVehicles(
      `  [${idx}] VehiculoID: ${vehicle.vehicleId}, ConductorID: ${
        vehicle.driverId
      }, OcupanteID: ${vehicle.vehicleOccupantId || 'N/A'}`
    );
  });

  const existingVehiclesMap = new Map(
    currentVehicleData.map((vehicle) => [vehicle.vehicleId, vehicle])
  );

  logVehicles(
    ` Mapa de vehículos existentes creado con ${existingVehiclesMap.size} entradas`
  );

  const toAdd = [];
  const toUpdate = [];

  for (const newVehicle of newVehicleData) {
    const existingVehicle = existingVehiclesMap.get(newVehicle.vehicleId);

    if (!existingVehicle) {
      logVehicles(
        ` Vehículo nuevo a añadir: ${newVehicle.vehicleId} (conductor: ${newVehicle.driverId})`
      );
      toAdd.push(newVehicle);
    } else {
      const hasDriverChange = newVehicle.driverId !== existingVehicle.driverId;

      if (hasDriverChange) {
        logVehicles(
          ` Vehículo existente a actualizar: ${newVehicle.vehicleId}`
        );
        logVehicles(
          `   Cambio de conductor: ${existingVehicle.driverId} → ${newVehicle.driverId}`
        );
        toUpdate.push(newVehicle);
      } else {
        logVehicles(
          ` Vehículo sin cambios: ${newVehicle.vehicleId} (conductor: ${newVehicle.driverId})`
        );
      }
    }
  }

  logVehicles(
    ` Procesamiento de vehículos - Total actuales: ${currentVehicleData.length}, Total nuevos: ${newVehicleData.length}`
  );
  logVehicles(
    ` Vehículos a añadir: ${toAdd.length}, Vehículos a actualizar: ${toUpdate.length}`
  );

  if (toAdd.length > 0) {
    logVehicles(' Detalle de vehículos a añadir:');
    toAdd.forEach((vehicle, idx) => {
      logVehicles(
        `  [${idx}] VehiculoID: ${vehicle.vehicleId}, ConductorID: ${vehicle.driverId}`
      );
    });
  }

  if (toUpdate.length > 0) {
    logVehicles(' Detalle de vehículos a actualizar:');
    toUpdate.forEach((vehicle, idx) => {
      logVehicles(
        `  [${idx}] VehiculoID: ${vehicle.vehicleId}, Nuevo ConductorID: ${vehicle.driverId}`
      );
    });
  }

  return {
    toAdd,
    toUpdate,
  };
};

export const findRemovedVehicleIds = (
  currentVehicleData: {
    vehicleId: string;
    vehicleOccupantId: string;
  }[],
  newVehicleData: {
    vehicleId: string;
    vehicleOccupantId: string;
  }[]
) => {
  logVehicles(' Iniciando findRemovedVehicleIds');
  logVehicles(
    ` Datos de entrada: ${currentVehicleData.length} vehículos actuales, ${newVehicleData.length} vehículos nuevos`
  );

  // Detailed logging of current vehicles
  logVehicles(' Vehículos actuales:');
  currentVehicleData.forEach((vehicle, idx) => {
    logVehicles(
      `  [${idx}] VehiculoID: ${vehicle.vehicleId}, OcupanteID: ${vehicle.vehicleOccupantId}`
    );
  });

  // Detailed logging of new vehicles
  logVehicles(' Vehículos nuevos:');
  newVehicleData.forEach((vehicle, idx) => {
    logVehicles(
      `  [${idx}] VehiculoID: ${vehicle.vehicleId}, OcupanteID: ${
        vehicle.vehicleOccupantId || 'N/A'
      }`
    );
  });

  // Crear conjuntos para comparación rápida
  const newVehicleIds = new Set(newVehicleData.map((v) => v.vehicleId));
  logVehicles(
    ` Conjunto de IDs de vehículos nuevos: [${[...newVehicleIds].join(', ')}]`
  );

  const removedIds = [];

  for (const currentVehicle of currentVehicleData) {
    if (!newVehicleIds.has(currentVehicle.vehicleId)) {
      logVehicles(
        ` Vehículo a eliminar: ${currentVehicle.vehicleId} (ocupante: ${currentVehicle.vehicleOccupantId})`
      );
      removedIds.push(currentVehicle.vehicleOccupantId);
    } else {
      logVehicles(` Vehículo mantenido: ${currentVehicle.vehicleId}`);
    }
  }

  logVehicles(` Encontrados ${removedIds.length} vehículos para eliminar`);
  if (removedIds.length > 0) {
    logVehicles(' IDs de ocupantes de vehículo a eliminar:', removedIds);
  }

  return removedIds;
};

// For externalWorks

export const findNewAndUpdatedExternalCompanies = (
  currentExternalCompaniesData: {
    externalCompanyIDs: string[];
    id: string;
    operatorsNumber: number;
  }[],
  newExternalCompaniesData: {
    externalCompanyIDs: string[];
    id: string;
    operatorsNumber: string;
    company: string;
  }[]
) => {
  // Create a Map from existing vehicle IDs for efficient lookups
  const existingCompaniesMap = new Map(
    currentExternalCompaniesData.map((company) => [
      company.externalCompanyIDs[0],
      company,
    ])
  );

  const worksToAdd = [];
  const worksToUpdate = [];

  for (const newCompany of newExternalCompaniesData) {
    const existingCompany = existingCompaniesMap.get(
      newCompany.externalCompanyIDs[0]
    );

    // New vehicle (no matching ID in current data)
    if (!existingCompany) {
      worksToAdd.push(newCompany);
    } else {
      // Existing vehicle with potential updates
      const hasDriverChange =
        Number(newCompany.operatorsNumber) !== existingCompany.operatorsNumber;

      if (hasDriverChange) {
        worksToUpdate.push({
          // El update necesita el id del externalWork (registro join action↔empresa),
          // NO el id de la empresa. `existingCompany` viene de GET_EXTERNAL_WORK_BY_ID →
          // su `.id` es el del externalWork. Usar `newCompany.id` (id de empresa)
          // provocaba P2025 "record not found" en prisma.externalWork.update().
          externalWorkId: existingCompany.id,
          operatorsNumber: Number(newCompany.operatorsNumber),
        });
      }
    }
  }

  logExternals(
    ` Procesamiento de empresas externas - Total actuales: ${currentExternalCompaniesData.length}, Total nuevas: ${newExternalCompaniesData.length}`
  );
  logExternals(
    ` Empresas a añadir: ${worksToAdd.length}, Empresas a actualizar: ${worksToUpdate.length}`
  );

  if (worksToAdd.length > 0) {
    logExternals(' Detalle de empresas externas a añadir:');
    worksToAdd.forEach((company, idx) => {
      logExternals(
        `  [${idx}] Empresa: ${company.company}, Operarios: ${company.operatorsNumber}`
      );
    });
  }

  if (worksToUpdate.length > 0) {
    logExternals(' Detalle de empresas externas a actualizar:');
    worksToUpdate.forEach((company, idx) => {
      logExternals(
        `  [${idx}] ID: ${company.externalWorkId}, Nuevos Operarios: ${company.operatorsNumber}`
      );
    });
  }

  return {
    worksToAdd,
    worksToUpdate,
  };
};

export const findRemovedExternalCompaniesIds = (
  currentExternalCompaniesData: {
    externalCompanyIDs: string[];
    id: string;
    operatorsNumber: number;
    workId: string;
  }[],
  newExternalCompaniesData: {
    externalCompanyIDs: string[];
    id: string;
    operatorsNumber: string;
  }[]
) => {
  const removedIds = [];

  for (const currentCompany of currentExternalCompaniesData) {
    if (
      !newExternalCompaniesData.some(
        (newCompany) =>
          newCompany.externalCompanyIDs[0] ===
          currentCompany.externalCompanyIDs[0]
      )
    ) {
      removedIds.push(currentCompany.id);
    }
  }

  logExternals(
    ` Encontradas ${removedIds.length} empresas externas para eliminar`
  );
  if (removedIds.length > 0) {
    logExternals(' IDs de empresas externas a eliminar:', removedIds);
  }

  return removedIds;
};

// For animals
export const findNewAndUpdatedAnimals = (
  currentAnimalsData: {
    statusId: string;
    ut: string;
    animalUnitId?: string;
    id: string;
  }[],
  newAnimalsData: {
    animalUnitId: string;
    statusId: string;
    ut: number;
    id: string;
  }[]
) => {
  const existingAnimalsMap = new Map(
    currentAnimalsData.map((animal) => [animal.id, animal])
  );

  const animalsToAdd = [];
  const animalsToUpdate = [];

  for (const newAnimal of newAnimalsData) {
    const existingAnimal = existingAnimalsMap.get(newAnimal.id);

    if (!existingAnimal) {
      animalsToAdd.push(newAnimal);
    } else {
      const hasDriverChange =
        newAnimal.statusId !== existingAnimal.statusId ||
        newAnimal.ut !== Number(existingAnimal.ut);

      if (hasDriverChange) {
        animalsToUpdate.push({
          // El id del unit sale del registro EXISTENTE (viene del servidor y
          // siempre lo tiene), con la fila del form como fallback. Al revés
          // rompía el replay tras un flap (GAP 6): el animal añadido en la
          // sesión ya existía en el servidor (lo creó la cola simple), el diff
          // lo clasificaba como update, pero su fila del form no tiene unitId →
          // UpdateManyAnimals con animalUnitId undefined → BAD_USER_INPUT y la
          // mutación entera rechazada. Mismo patrón que externalWorkId arriba.
          animalUnitId: existingAnimal.animalUnitId ?? newAnimal.animalUnitId,
          newValue: Number(newAnimal.ut),
          animalStatusId: newAnimal.statusId,
        });
      }
    }
  }

  logAnimals(
    ` Procesamiento de animales - Total actuales: ${currentAnimalsData.length}, Total nuevos: ${newAnimalsData.length}`
  );
  logAnimals(
    ` Animales a añadir: ${animalsToAdd.length}, Animales a actualizar: ${animalsToUpdate.length}`
  );

  if (animalsToAdd.length > 0) {
    logAnimals(' Detalle de animales a añadir:');
    animalsToAdd.forEach((animal, idx) => {
      logAnimals(
        `  [${idx}] ID: ${animal.id}, Estado: ${animal.statusId}, UT: ${animal.ut}`
      );
    });
  }

  if (animalsToUpdate.length > 0) {
    logAnimals(' Detalle de animales a actualizar:');
    animalsToUpdate.forEach((animal, idx) => {
      logAnimals(
        `  [${idx}] ID: ${animal.animalUnitId}, Nuevo estado: ${animal.animalStatusId}, Nueva UT: ${animal.newValue}`
      );
    });
  }

  return {
    animalsToAdd,
    animalsToUpdate,
  };
};

export const findRemovedAnimalIds = (
  currentAnimalsData: {
    id: string;
    animalUnitId: string;
  }[],
  newAnimalsData: {
    id: string;
    animalUnitId: string;
  }[]
) => {
  const removedIds = [];

  for (const currentAnimal of currentAnimalsData) {
    if (
      !newAnimalsData.some((newAnimal) => newAnimal.id === currentAnimal.id)
    ) {
      removedIds.push(currentAnimal.animalUnitId);
    }
  }

  logAnimals(` Encontrados ${removedIds.length} animales para eliminar`);
  if (removedIds.length > 0) {
    logAnimals(' IDs de animales a eliminar:', removedIds);
  }

  return removedIds;
};
